import React, { useState } from 'react';
import { X, CheckCircle, Loader2 } from 'lucide-react';
import { supabase } from '../lib/supabaseClient';

interface FakeDoorModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess?: () => void;
}

export const FakeDoorModal: React.FC<FakeDoorModalProps> = ({ isOpen, onClose, onSuccess }) => {
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [formData, setFormData] = useState({
        email: '',
        role: 'Director OTRI',
        pain_point: 'Información dispersa',
    });

    if (!isOpen) return null;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);

        try {
            const { error } = await supabase
                .from('leads')
                .insert([
                    {
                        email: formData.email,
                        role: formData.role,
                        pain_point: formData.pain_point,
                    },
                ]);

            if (error) throw error;

            setSuccess(true);
            setTimeout(() => {
                if (onSuccess) {
                    onSuccess();
                }
                onClose();
                setSuccess(false);
                setFormData({
                    email: '',
                    role: 'Director OTRI',
                    pain_point: 'Información dispersa',
                });
            }, 3000);
        } catch (error) {
            console.error('Error saving lead:', error);
            alert('Hubo un error al guardar tus datos. Por favor intenta de nuevo.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
            <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-2xl">
                <div className="mb-4 flex items-center justify-between">
                    <h2 className="text-xl font-bold text-gray-900">
                        {success ? '¡Registro Exitoso!' : 'Únete a la lista de espera exclusiva'}
                    </h2>
                    <button
                        onClick={onClose}
                        className="rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                    >
                        <X size={20} />
                    </button>
                </div>

                {success ? (
                    <div className="flex flex-col items-center justify-center py-8 text-center">
                        <CheckCircle className="mb-4 h-16 w-16 text-green-500" />
                        <p className="text-lg font-medium text-gray-800">¡Estás en la lista!</p>
                        <p className="text-gray-600">Te avisaremos pronto.</p>
                        <p className="mt-2 text-sm text-gray-500">Gracias. Te contactaremos pronto para tu prueba gratuita de 3 meses.</p>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label htmlFor="email" className="mb-1 block text-sm font-medium text-gray-700">
                                Email Corporativo
                            </label>
                            <input
                                type="email"
                                id="email"
                                required
                                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                placeholder="nombre@universidad.edu"
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            />
                        </div>

                        <div>
                            <label htmlFor="role" className="mb-1 block text-sm font-medium text-gray-700">
                                Rol
                            </label>
                            <select
                                id="role"
                                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                value={formData.role}
                                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                            >
                                <option value="Director OTRI">Director OTRI</option>
                                <option value="Gestor TTO">Gestor TTO</option>
                                <option value="Investigador">Investigador</option>
                                <option value="Empresa/Inversor">Empresa/Inversor</option>
                            </select>
                        </div>

                        <div>
                            <label htmlFor="pain_point" className="mb-1 block text-sm font-medium text-gray-700">
                                ¿Cuál es tu mayor desafío?
                            </label>
                            <select
                                id="pain_point"
                                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                value={formData.pain_point}
                                onChange={(e) => setFormData({ ...formData, pain_point: e.target.value })}
                            >
                                <option value="Información dispersa">Información dispersa</option>
                                <option value="Seguimiento de patentes">Seguimiento de patentes</option>
                                <option value="Comercialización">Comercialización</option>
                                <option value="Burocracia">Burocracia</option>
                            </select>
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            className="mt-4 flex w-full items-center justify-center rounded-lg bg-blue-600 px-4 py-3 font-semibold text-white transition-colors hover:bg-blue-700 disabled:opacity-70"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                                    Procesando...
                                </>
                            ) : (
                                'Unirme ahora'
                            )}
                        </button>
                    </form>
                )}
            </div>
        </div>
    );
};
